import { create as createStore } from "zustand";
import { create } from "@bufbuild/protobuf";
import { preloadCardImages } from "../assets/cards";
import {
  Phase,
  Role,
  RoomSnapshotSchema,
  type RoomSnapshot,
  type ServerEnvelope,
} from "../gen/game/v1/envelope_pb";
import {
  changeAvatarCommand,
  continueAfterMatchCommand,
  createRoomCommand,
  dealerTurnCommand,
  helloCommand,
  joinRoomCommand,
  leaveRoomCommand,
  loadedCommand,
  playerTurnCommand,
  playerVoteCommand,
  resumeCommand,
  skipTutorialCommand,
  startGameCommand,
} from "../proto/commands";
import { GameSocket, type ConnectionStatus } from "../net/wsClient";

type ClientMode = "screen" | "player";

type GameState = {
  status: ConnectionStatus;
  joinStatus: "idle" | "pending" | "succeeded" | "failed";
  url: string;
  mode: ClientMode;
  playerName: string;
  playerId: string;
  sessionToken: string;
  roomCode: string;
  snapshot: RoomSnapshot | null;
  isPaused: boolean;
  hand: string[];
  tableCards: string[];
  phrase: string;
  dealerCard: string;
  playedPlayerIds: string[];
  votedPlayerIds: string[];
  ownPlayedCardId: string;
  playerPlays: Record<string, string>;
  playerVotes: Record<string, string>;
  playerResultDelta: Record<string, number>;
  playerResults: Record<string, number>;
  tutorialSkipVotedPlayerIds: string[];
  tutorialSkipVotesCount: number;
  tutorialSkipPlayersCount: number;
  tutorialSkipRemainingCount: number;
  postMatchContinuedPlayerIds: string[];
  postMatchContinuedCount: number;
  postMatchPlayersCount: number;
  postMatchRemainingCount: number;
  error: string;
  log: string[];
  connect(url: string, mode: ClientMode, playerName?: string): void;
  disconnect(): void;
  createRoom(): void;
  joinRoom(code: string, name: string): void;
  leaveRoom(): void;
  startGame(): void;
  loaded(): void;
  skipTutorial(): void;
  continueAfterMatch(): void;
  submitDealerTurn(cardId: string, phrase: string): void;
  playCard(cardId: string): void;
  vote(cardId: string): void;
  changeAvatar(avatarId: string): void;
  resetSession(): void;
  resumeConnection(): void;
  setMode(mode: ClientMode): void;
  setPlayerName(name: string): void;
};

let seq = 0n;
const nextSeq = () => {
  seq += 1n;
  return seq;
};

const sessionRoomCodeKey = "p1.session.roomCode";
const sessionPlayerIdKey = "p1.session.playerId";
const sessionTokenKey = "p1.session.token";
const sessionModeKey = "p1.session.mode";
const sessionKeys = [
  sessionModeKey,
  sessionRoomCodeKey,
  sessionPlayerIdKey,
  sessionTokenKey,
];

let reconnectTimer = 0;
let manualDisconnect = false;

function debugLog(event: string, data: Record<string, unknown> = {}) {
  if (!new URLSearchParams(window.location.search).has("debug")) {
    return;
  }

  console.info(`[p1-store] ${event}`, data);
}

const socket = new GameSocket({
  onStatus: (status) => {
    debugLog("socket status", {
      status,
      manualDisconnect,
      reconnectTimer,
      roomCode: useGameStore.getState().roomCode,
      playerId: useGameStore.getState().playerId,
      joinStatus: useGameStore.getState().joinStatus,
    });
    useGameStore.setState({ status });
    if (status === "closed" || status === "error") {
      scheduleReconnect();
    }
  },
  onMessage: (message) => useGameStore.getState().handleServerMessage(message),
  onError: (error) => {
    debugLog("socket error", { error });
    useGameStore.setState((state) => ({
      error,
      log: [error, ...state.log].slice(0, 20),
    }));
  },
});

type InternalGameState = GameState & {
  handleServerMessage(message: ServerEnvelope): void;
};

export const useGameStore = createStore<InternalGameState>((set, get) => ({
  status: "idle",
  joinStatus: "idle",
  url: defaultWsUrl(),
  mode: readStoredMode(),
  playerName: "",
  playerId: readStoredValue(sessionPlayerIdKey),
  sessionToken: readStoredValue(sessionTokenKey),
  roomCode: readStoredValue(sessionRoomCodeKey),
  snapshot: null,
  isPaused: false,
  hand: [],
  tableCards: [],
  phrase: "",
  dealerCard: "",
  playedPlayerIds: [],
  votedPlayerIds: [],
  ownPlayedCardId: "",
  playerPlays: {},
  playerVotes: {},
  playerResultDelta: {},
  playerResults: {},
  tutorialSkipVotedPlayerIds: [],
  tutorialSkipVotesCount: 0,
  tutorialSkipPlayersCount: 0,
  tutorialSkipRemainingCount: 0,
  postMatchContinuedPlayerIds: [],
  postMatchContinuedCount: 0,
  postMatchPlayersCount: 0,
  postMatchRemainingCount: 0,
  error: "",
  log: [],

  connect(url, mode, playerName = "") {
    manualDisconnect = false;
    debugLog("connect", {
      url,
      mode,
      hasPlayerName: playerName.trim() !== "",
      previousStatus: get().status,
      roomCode: get().roomCode,
      playerId: get().playerId,
    });
    set({ url, mode, playerName, error: "" });
    socket.connect(url, () => {
      const role = mode === "screen" ? Role.SCREEN : Role.PLAYER;
      debugLog("send hello", { role, hasPlayerName: playerName.trim() !== "" });
      socket.send(helloCommand(nextSeq(), role, playerName));
    });
  },

  disconnect() {
    manualDisconnect = true;
    debugLog("disconnect", {
      status: get().status,
      roomCode: get().roomCode,
      playerId: get().playerId,
      joinStatus: get().joinStatus,
    });
    clearReconnectTimer();
    socket.disconnect();
    set({ status: "closed" });
  },

  createRoom() {
    socket.send(createRoomCommand(nextSeq()));
  },

  joinRoom(code, name) {
    debugLog("send joinRoom", {
      code: code.trim().toUpperCase(),
      hasName: name.trim() !== "",
      status: get().status,
      currentRoomCode: get().roomCode,
      currentPlayerId: get().playerId,
    });
    set({ joinStatus: "pending", error: "" });
    socket.send(
      joinRoomCommand(nextSeq(), code.trim().toUpperCase(), name.trim()),
    );
  },

  leaveRoom() {
    const state = get();
    if (!state.roomCode || state.status !== "connected") {
      debugLog("skip leaveRoom", {
        roomCode: state.roomCode,
        status: state.status,
      });
      return;
    }

    debugLog("send leaveRoom", {
      roomCode: state.roomCode,
      playerId: state.playerId,
    });
    socket.send(leaveRoomCommand(nextSeq()));
  },

  startGame() {
    socket.send(startGameCommand(nextSeq()));
  },

  loaded() {
    socket.send(loadedCommand(nextSeq()));
  },

  skipTutorial() {
    socket.send(skipTutorialCommand(nextSeq()));
  },

  continueAfterMatch() {
    socket.send(continueAfterMatchCommand(nextSeq()));
  },

  submitDealerTurn(cardId, phrase) {
    socket.send(dealerTurnCommand(nextSeq(), cardId, phrase.trim()));
  },

  playCard(cardId) {
    set({ ownPlayedCardId: cardId });
    socket.send(playerTurnCommand(nextSeq(), cardId));
  },

  vote(cardId) {
    socket.send(playerVoteCommand(nextSeq(), cardId));
  },

  changeAvatar(avatarId) {
    socket.send(changeAvatarCommand(nextSeq(), avatarId));
  },

  resetSession() {
    debugLog("resetSession", {
      status: get().status,
      roomCode: get().roomCode,
      playerId: get().playerId,
      joinStatus: get().joinStatus,
    });
    clearStoredSession();
    set({
      status: "idle",
      joinStatus: "idle",
      mode: "player",
      playerId: "",
      sessionToken: "",
      roomCode: "",
      snapshot: null,
      isPaused: false,
      hand: [],
      tableCards: [],
      phrase: "",
      dealerCard: "",
      playedPlayerIds: [],
      votedPlayerIds: [],
      ownPlayedCardId: "",
      playerPlays: {},
      playerVotes: {},
      playerResultDelta: {},
      playerResults: {},
      tutorialSkipVotedPlayerIds: [],
      tutorialSkipVotesCount: 0,
      tutorialSkipPlayersCount: 0,
      tutorialSkipRemainingCount: 0,
      postMatchContinuedPlayerIds: [],
      postMatchContinuedCount: 0,
      postMatchPlayersCount: 0,
      postMatchRemainingCount: 0,
      error: "",
      log: [],
    });
  },

  resumeConnection() {
    manualDisconnect = false;
    clearReconnectTimer();
    const state = get();
    if (!canResume(state)) {
      debugLog("skip resumeConnection", {
        roomCode: state.roomCode,
        playerId: state.playerId,
        hasSessionToken: state.sessionToken !== "",
        mode: state.mode,
        status: state.status,
      });
      return;
    }

    debugLog("resumeConnection", {
      url: state.url,
      mode: state.mode,
      roomCode: state.roomCode,
      playerId: state.playerId,
      status: state.status,
    });
    socket.connect(state.url, () => {
      const role = state.mode === "screen" ? Role.SCREEN : Role.PLAYER;
      debugLog("send resume", {
        role,
        roomCode: state.roomCode,
        playerId: state.playerId,
      });
      socket.send(
        resumeCommand(
          nextSeq(),
          role,
          state.roomCode,
          state.playerId,
          state.sessionToken,
        ),
      );
    });
  },

  setMode(mode) {
    set({ mode });
  },

  setPlayerName(playerName) {
    set({ playerName });
  },

  handleServerMessage(message) {
    const body = message.body;
    if (!body.case) {
      return;
    }

    debugLog("message", {
      case: body.case,
      roomCode: get().roomCode,
      playerId: get().playerId,
      joinStatus: get().joinStatus,
      status: get().status,
    });

    set((state) => ({
      log: [`${body.case}`, ...state.log].slice(0, 20),
    }));

    switch (body.case) {
      case "createRoomAck":
        set({
          roomCode: body.value.roomCode,
          sessionToken: body.value.sessionToken,
          snapshot: body.value.snapshot ?? null,
          isPaused: false,
          error: ackError(body.value.meta),
        });
        persistSession({
          mode: "screen",
          roomCode: body.value.roomCode,
          playerId: "",
          sessionToken: body.value.sessionToken,
        });
        break;
      case "joinRoomAck":
        if (!body.value.meta?.ok) {
          debugLog("joinRoomAck failed", {
            error: ackError(body.value.meta),
          });
          set({
            joinStatus: "failed",
            roomCode: "",
            playerId: "",
            snapshot: null,
            isPaused: false,
            error: ackError(body.value.meta),
          });
          break;
        }

        debugLog("joinRoomAck ok", {
          roomCode: body.value.roomCode,
          playerId: body.value.playerId,
          players: body.value.snapshot?.players.length ?? 0,
        });
        set({
          joinStatus: "succeeded",
          roomCode: body.value.roomCode,
          playerId: body.value.playerId,
          sessionToken: body.value.sessionToken,
          snapshot: body.value.snapshot ?? null,
          isPaused: false,
          error: "",
        });
        persistSession({
          mode: "player",
          roomCode: body.value.roomCode,
          playerId: body.value.playerId,
          sessionToken: body.value.sessionToken,
        });
        break;
      case "startGameAck":
      case "loadedAck":
      case "skipTutorialAck":
      case "continueAfterMatchAck":
      case "dealerTurnAck":
      case "playerTurnAck":
      case "playerVoteAck":
      case "changeAvatarAck":
      case "leaveRoomAck":
        set({ error: ackError(body.value.meta) });
        break;
      case "resumeAck":
        if (!body.value.meta?.ok) {
          const error = ackError(body.value.meta);
          debugLog("resumeAck failed", { error });
          clearStoredSession();
          manualDisconnect = true;
          clearReconnectTimer();
          socket.disconnect();
          set({
            status: "closed",
            joinStatus: "idle",
            playerId: "",
            sessionToken: "",
            roomCode: "",
            snapshot: null,
            isPaused: false,
            hand: [],
            tableCards: [],
            phrase: "",
            dealerCard: "",
            playedPlayerIds: [],
            votedPlayerIds: [],
            ownPlayedCardId: "",
            playerPlays: {},
            playerVotes: {},
            playerResultDelta: {},
            playerResults: {},
            tutorialSkipVotedPlayerIds: [],
            tutorialSkipVotesCount: 0,
            tutorialSkipPlayersCount: 0,
            tutorialSkipRemainingCount: 0,
            postMatchContinuedPlayerIds: [],
            postMatchContinuedCount: 0,
            postMatchPlayersCount: 0,
            postMatchRemainingCount: 0,
            error,
          });
          break;
        }

        debugLog("resumeAck ok", {
          roomCode: body.value.roomCode,
          playerId: body.value.playerId || get().playerId,
          paused: body.value.paused,
        });
        set((state) => ({
          status: "connected",
          joinStatus: "succeeded",
          roomCode: body.value.roomCode,
          playerId: body.value.playerId || state.playerId,
          snapshot: body.value.snapshot ?? state.snapshot,
          hand: body.value.privateState?.hand ?? state.hand,
          ownPlayedCardId:
            body.value.privateState?.ownPlayedCardId ?? state.ownPlayedCardId,
          phrase: body.value.privateState?.phrase ?? state.phrase,
          dealerCard: body.value.privateState?.dealerCard ?? state.dealerCard,
          tableCards: body.value.privateState?.tableCards ?? state.tableCards,
          playerPlays:
            body.value.privateState?.playerPlays ?? state.playerPlays,
          playerVotes:
            body.value.privateState?.playerVotes ?? state.playerVotes,
          playedPlayerIds: Object.keys(
            body.value.privateState?.playerPlays ?? state.playerPlays,
          ),
          votedPlayerIds: Object.keys(
            body.value.privateState?.playerVotes ?? state.playerVotes,
          ),
          playerResultDelta:
            body.value.privateState?.playerResultDelta?.byPlayerId ??
            state.playerResultDelta,
          playerResults:
            body.value.privateState?.playerResults?.byPlayerId ??
            state.playerResults,
          isPaused: body.value.paused,
          error: "",
        }));
        break;
      case "lobbyUpdated":
        set({
          roomCode: body.value.roomCode,
          snapshot: body.value.snapshot ?? null,
          isPaused: false,
          tutorialSkipVotedPlayerIds: [],
          tutorialSkipVotesCount: 0,
          tutorialSkipPlayersCount: 0,
          tutorialSkipRemainingCount: 0,
          postMatchContinuedPlayerIds: [],
          postMatchContinuedCount: 0,
          postMatchPlayersCount: 0,
          postMatchRemainingCount: 0,
        });
        break;
      case "playerReadyChanged":
        set((state) => ({
          snapshot: state.snapshot
            ? {
                ...state.snapshot,
                players: state.snapshot.players.map((player) =>
                  player.playerId === body.value.playerId
                    ? { ...player, ready: body.value.ready }
                    : player,
                ),
              }
            : state.snapshot,
        }));
        break;
      case "playerAvatarChanged":
        set((state) => ({
          roomCode: body.value.roomCode,
          snapshot: state.snapshot
            ? {
                ...state.snapshot,
                players: state.snapshot.players.map((player) =>
                  player.playerId === body.value.playerId
                    ? { ...player, avatarId: body.value.avatarId }
                    : player,
                ),
              }
            : state.snapshot,
        }));
        break;
      case "matchStarted":
        set((state) => ({
          roomCode: body.value.roomCode,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
            leaderId: body.value.leaderId,
            dealerId: body.value.dealerId,
            roundNo: body.value.roundNo,
            maxRound: body.value.maxRound,
          }),
        }));
        break;
      case "phaseUpdated":
        set((state) => ({
          roomCode: body.value.roomCode,
          playedPlayerIds:
            body.value.phase === Phase.PLAYERS_TURN
              ? []
              : state.playedPlayerIds,
          votedPlayerIds:
            body.value.phase === Phase.VOTING ? [] : state.votedPlayerIds,
          tutorialSkipVotedPlayerIds:
            body.value.phase === Phase.TUTORIAL
              ? state.tutorialSkipVotedPlayerIds
              : [],
          tutorialSkipVotesCount:
            body.value.phase === Phase.TUTORIAL
              ? state.tutorialSkipVotesCount
              : 0,
          tutorialSkipPlayersCount:
            body.value.phase === Phase.TUTORIAL
              ? state.tutorialSkipPlayersCount
              : 0,
          tutorialSkipRemainingCount:
            body.value.phase === Phase.TUTORIAL
              ? state.tutorialSkipRemainingCount
              : 0,
          postMatchContinuedPlayerIds:
            body.value.phase === Phase.POST_MATCH
              ? state.postMatchContinuedPlayerIds
              : [],
          postMatchContinuedCount:
            body.value.phase === Phase.POST_MATCH
              ? state.postMatchContinuedCount
              : 0,
          postMatchPlayersCount:
            body.value.phase === Phase.POST_MATCH
              ? state.postMatchPlayersCount
              : 0,
          postMatchRemainingCount:
            body.value.phase === Phase.POST_MATCH
              ? state.postMatchRemainingCount
              : 0,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
            leaderId: body.value.leaderId,
            dealerId: body.value.dealerId,
            roundNo: body.value.roundNo,
            maxRound: body.value.maxRound,
            hasDeadline: body.value.hasDeadline,
            deadlineUnixMs: body.value.deadlineUnixMs,
          }),
          isPaused: false,
        }));
        break;
      case "roomPaused":
        set({ roomCode: body.value.roomCode, isPaused: true });
        break;
      case "dealerSelected":
        set((state) => ({
          roomCode: body.value.roomCode,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
            dealerId: body.value.dealerId,
          }),
        }));
        break;
      case "tutorialSkipped":
        set((state) => ({
          roomCode: body.value.roomCode,
          tutorialSkipVotedPlayerIds: [],
          tutorialSkipVotesCount: 0,
          tutorialSkipPlayersCount: 0,
          tutorialSkipRemainingCount: 0,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "tutorialSkipStatus":
        set((state) => ({
          roomCode: body.value.roomCode,
          tutorialSkipVotedPlayerIds: body.value.votedPlayerIds,
          tutorialSkipVotesCount: body.value.votesCount,
          tutorialSkipPlayersCount: body.value.playersCount,
          tutorialSkipRemainingCount: body.value.remainingCount,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "handDealt":
        set({ playerId: body.value.playerId, hand: body.value.cards });
        break;
      case "cardsPreload":
        preloadCardImages(body.value.cardIds);
        break;
      case "phraseShown":
        set((state) => ({
          roomCode: body.value.roomCode,
          phrase: body.value.phrase,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "playerPlayed":
        set((state) => ({
          roomCode: body.value.roomCode,
          playedPlayerIds: addUnique(
            state.playedPlayerIds,
            body.value.playerId,
          ),
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "playerVoted":
        set((state) => ({
          roomCode: body.value.roomCode,
          votedPlayerIds: addUnique(state.votedPlayerIds, body.value.playerId),
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "cardsShown":
        set((state) => ({
          roomCode: body.value.roomCode,
          tableCards: body.value.cards,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "votingResult":
        set((state) => ({
          roomCode: body.value.roomCode,
          dealerCard: body.value.dealerCard,
          playerPlays: body.value.playerPlays,
          playerVotes: body.value.playerVotes,
          playedPlayerIds: Object.keys(body.value.playerPlays),
          votedPlayerIds: Object.keys(body.value.playerVotes),
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "roundResult":
        set((state) => ({
          roomCode: body.value.roomCode,
          playerResultDelta: body.value.playerResultDelta?.byPlayerId ?? {},
          playerResults: body.value.playerResults?.byPlayerId ?? {},
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "matchResult":
        set((state) => ({
          roomCode: body.value.roomCode,
          playerResults:
            body.value.playerResults?.byPlayerId ?? state.playerResults,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "postMatchStatus":
        set((state) => ({
          roomCode: body.value.roomCode,
          postMatchContinuedPlayerIds: body.value.continuedPlayerIds,
          postMatchContinuedCount: body.value.continuedCount,
          postMatchPlayersCount: body.value.playersCount,
          postMatchRemainingCount: body.value.remainingCount,
          snapshot: mergeSnapshot(state.snapshot, {
            roomCode: body.value.roomCode,
            phase: body.value.phase,
          }),
        }));
        break;
      case "roomClosed":
        debugLog("roomClosed", {
          roomCode: get().roomCode,
          playerId: get().playerId,
          reason: body.value.reason,
        });
        clearStoredSession();
        manualDisconnect = true;
        clearReconnectTimer();
        set({
          joinStatus: "idle",
          mode: "player",
          playerId: "",
          sessionToken: "",
          roomCode: "",
          snapshot: null,
          isPaused: false,
          hand: [],
          tableCards: [],
          phrase: "",
          dealerCard: "",
          playedPlayerIds: [],
          votedPlayerIds: [],
          ownPlayedCardId: "",
          playerPlays: {},
          playerVotes: {},
          playerResultDelta: {},
          playerResults: {},
          tutorialSkipVotedPlayerIds: [],
          tutorialSkipVotesCount: 0,
          tutorialSkipPlayersCount: 0,
          tutorialSkipRemainingCount: 0,
          postMatchContinuedPlayerIds: [],
          postMatchContinuedCount: 0,
          postMatchPlayersCount: 0,
          postMatchRemainingCount: 0,
          error: `Room closed: ${body.value.reason}`,
        });
        break;
    }
  },
}));

function ackError(
  meta: { ok: boolean; error?: { message: string } } | undefined,
): string {
  if (!meta || meta.ok) {
    return "";
  }

  const message = meta.error?.message || "Command rejected";

  if (message.includes("room not found")) {
    return "Комната не найдена";
  }

  return message;
}

function mergeSnapshot(
  snapshot: RoomSnapshot | null,
  patch: Partial<RoomSnapshot>,
): RoomSnapshot {
  const base =
    snapshot ??
    create(RoomSnapshotSchema, {
      roomCode: "",
      phase: Phase.LOBBY,
      leaderId: "",
      dealerId: "",
      players: [],
      roundNo: 0,
      maxRound: 0,
      hasDeadline: false,
      deadlineUnixMs: 0n,
    });

  return {
    ...base,
    ...patch,
  };
}

function addUnique(items: string[], item: string): string[] {
  if (!item || items.includes(item)) {
    return items;
  }

  return [...items, item];
}

function scheduleReconnect() {
  if (manualDisconnect || reconnectTimer !== 0) {
    return;
  }

  const state = useGameStore.getState();
  if (!canResume(state)) {
    return;
  }

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = 0;
    useGameStore.getState().resumeConnection();
  }, 1000);
}

function clearReconnectTimer() {
  if (!reconnectTimer) {
    return;
  }

  window.clearTimeout(reconnectTimer);
  reconnectTimer = 0;
}

function canResume(state: GameState) {
  if (!state.roomCode || !state.sessionToken) {
    return false;
  }

  return state.mode === "screen" || state.playerId !== "";
}

function persistSession(session: {
  mode: ClientMode;
  roomCode: string;
  playerId: string;
  sessionToken: string;
}) {
  if (!session.roomCode || !session.sessionToken) {
    return;
  }

  writeStoredValue(sessionModeKey, session.mode);
  writeStoredValue(sessionRoomCodeKey, session.roomCode);
  writeStoredValue(sessionPlayerIdKey, session.playerId);
  writeStoredValue(sessionTokenKey, session.sessionToken);
}

function clearStoredSession() {
  for (const key of sessionKeys) {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }
}

function readStoredMode(): ClientMode {
  return readStoredValue(sessionModeKey) === "screen" ? "screen" : "player";
}

function readStoredValue(key: string): string {
  return sessionStorage.getItem(key) ?? localStorage.getItem(key) ?? "";
}

function writeStoredValue(key: string, value: string) {
  sessionStorage.setItem(key, value);
  localStorage.setItem(key, value);
}

function defaultWsUrl() {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const host =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
      ? `${window.location.hostname}:8080`
      : window.location.host;

  return `${protocol}://${host}/ws`;
}
